import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Box,
	Button,
	createStyles,
	FileButton,
	Flex,
	Icon,
	Input,
	Loader,
	Stack,
	Textarea,
	TFNotifier,
	TFSection,
	Tooltip,
} from '@tf/ui';
import type { ObjectRelationValue } from '@tf/utils';
import { objectRelationValue } from '@tf/utils';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import pdfJSWorkerURL from 'pdfjs-dist/build/pdf.worker?url';
import { useEffect, useRef, useState } from 'react';
import { Controller, useController, useFormContext } from 'react-hook-form';

import { useSaveDocumentCheckIdMutation } from '../../../../api/documentCheck';
import type { FileInfo } from '../../../../api/files';
import { getFilesInfo, uploadFile } from '../../../../api/files';
import { useConfigContext, useSegmentContext } from '../../../../hooks';
import { TooltipIcon } from '../../TooltipIcon';
import type { DefaultInputProps } from '../../types';
import { DocumentItem } from './DocumentItem';
import { DocumentsCheckComponent } from './extensions/DocumentsCheck';
import { getPdfUploadOrder, uploadFilesInOrder } from './utils';

GlobalWorkerOptions.workerSrc = pdfJSWorkerURL;

enum NotificationKey {
	UploadSuccess = 'upload-success',
	UploadError = 'upload-error',
	DeleteSuccess = 'delete-success',
}

const useStyles = createStyles(({ colors, radius }) => ({
	uploadButton: {
		fontWeight: 500,
		fontSize: 13,
		border: `1px solid transparent`,
		backgroundColor: colors.gray[1],
		'&:disabled': {
			color: colors.gray[5],
			backgroundColor: colors.gray[1],
		},
	},
	uploadButton__error: {
		border: `1px solid ${colors.red[3]}`,
		backgroundColor: colors.red[1],
		color: colors.dark[5],
		'&:not([data-disabled]):hover': {
			backgroundColor: colors.red[2],
		},
		borderColor: colors.red[1],
	},
	switchButton: {
		'&:hover': {
			backgroundColor: 'transparent',
			color: colors.brand[7],
		},
	},
	textInputDisabled: {
		textArea: {
			cursor: 'default',
			backgroundColor: colors.gray[1],
			borderColor: colors.gray[1],
			borderBottomWidth: 1,
			borderBottomColor: colors.gray[2],
			'&:focus': {
				borderColor: colors.gray[1],
				borderBottomWidth: 1,
				borderBottomColor: colors.gray[8],
			},
		},
	},
	emptyWrapper: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '.75rem',
		borderRadius: radius.sm,
		backgroundColor: colors.gray[1],
	},
	emptyContent: {
		flex: 1,
		color: colors.dark[6],
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		fontSize: 13,
		textOverflow: 'ellipsis',
		marginRight: '.5rem',
	},
}));

interface FilesState {
	files: ObjectRelationValue<'form_attachment'>[];
	is_no_document: boolean;
	reason: string;
	document_check_job?: ObjectRelationValue<'document_check'>;
}

export interface Props extends DefaultInputProps {
	extensions?: string[];
	fieldPath: string;
	isPDFToPNGEnabled?: boolean;
}

export default function FileInput({
	name,
	label,
	extensions = [],
	isReadOnly,
	isRequired,
	tooltip,
	isPDFToPNGEnabled,
}: Props) {
	const formContext = useFormContext();
	const { classes } = useStyles();
	const { _ } = useLingui();
	const { clusterId } = useConfigContext((s) => s.apiConfig);

	const { identity } = useSegmentContext();

	const [document, setDocument] = useState<FilesState>({
		files: formContext.getValues(`${name}.files`) ?? [],
		is_no_document: formContext.getValues(`${name}.is_no_document`) ?? false,
		reason: formContext.getValues(`${name}.reason`) ?? '',
		document_check_job: formContext.getValues(`${name}.document_check_job`) ?? {},
	});

	useEffect(() => {
		formContext.setValue(`${name}.files`, document.files, { shouldDirty: true });
		formContext.setValue(`${name}.is_no_document`, document.is_no_document, { shouldDirty: true });
		if (
			extensions.includes('DOCUMENT_CHECK') &&
			document.document_check_job &&
			Object.keys(document.document_check_job).length > 0
		) {
			formContext.setValue(`${name}.document_check_job`, document.document_check_job, {
				shouldDirty: true,
			});
		}
	}, [document, name, formContext.setValue, extensions.includes]);

	const filesInfoQuery = useQuery({
		enabled: document.files.length > 0,
		queryKey: ['filesInfo', name, document.files],
		queryFn: () => getFilesInfo(document.files.map((f) => f._object)),
	});

	const uploadFileMutation = useMutation({
		mutationFn: uploadFile,
	});

	const handleFilesUpload = async (files: File[]) => {
		const uploadedFileIds = await uploadFilesInOrder(files, (file) =>
			uploadFileMutation.mutateAsync({
				clusterId,
				file,
			}),
		);
		if (!uploadedFileIds) {
			TFNotifier.error(_(msg({ id: 'fb.fileInput.notifications.uploadFailed', message: 'Document upload failed' })), {
				id: NotificationKey.UploadError,
			});
			return;
		}
		setDocument((prevState) => ({
			...prevState,
			files: [...prevState.files, ...uploadedFileIds.map((id) => objectRelationValue('form_attachment', id))],
		}));
		resetFileRef.current?.();
	};

	const handleFileUpload = async (file: File | null) => {
		if (!file) return;
		await handleFilesUpload([file]);
	};

	const resetFileRef = useRef<() => void>(null);
	const handleFileDelete = (fileToDelete: FileInfo) => {
		setDocument((prevState) => ({
			...prevState,
			files: prevState.files.filter((f) => f._object !== fileToDelete.id),
		}));
		resetFileRef.current?.();
	};

	const saveDocumentCheckIdMutation = useSaveDocumentCheckIdMutation();
	const handleDocumentCheckCreated = (documentCheckId: string) => {
		saveDocumentCheckIdMutation.mutate({ segmentIdentity: identity, fieldPath: name, documentCheckId });
		setDocument((prevState) => ({
			...prevState,
			document_check_job: objectRelationValue('document_check', documentCheckId),
		}));
	};

	const toggleAvailability = () => {
		const is_no_document = !document.is_no_document;

		setDocument((prevState) => ({ ...prevState, is_no_document }));
		if (!is_no_document) {
			formContext.setValue(`${name}.reason`, undefined, { shouldDirty: true });
		}
	};

	const {
		fieldState: { error: reasonError },
	} = useController({
		control: formContext.control,
		name: `${name}.reason`,
	});

	const isDocumentCheck = extensions.includes('DOCUMENT_CHECK');

	// inspired by https://medium.com/@charanvinaynarni/pdf-to-image-conversion-using-reactjs-fd250a25bf05
	const renderPages = async (data: string, fileName: string) => {
		const filesList: File[] = [];
		const canvas = window.document.createElement('canvas');
		const pdfProxy = await getDocument({ data }).promise;

		for (let i = 1; i <= pdfProxy.numPages; i++) {
			const page = await pdfProxy.getPage(i);
			const viewport = page.getViewport({ scale: 2 });
			canvas.height = viewport.height;
			canvas.width = viewport.width;
			// pdfjs 6 takes the canvas itself; canvasContext is the legacy path
			// and requires canvas to be null, which defeats the purpose here.
			const renderContext = {
				canvas,
				viewport,
			};
			await page.render(renderContext).promise;

			const imgUrl = canvas.toDataURL('image/png');
			const file = base64ToPngFile(imgUrl, `${fileName} (page ${i}).png`);

			filesList.push(file);
		}

		return filesList;
	};

	const uploadPDF = async (pdf: File) => {
		const fileName = pdf.name.replace('.pdf', '');
		const response = await fetch(URL.createObjectURL(pdf));
		const blob = await response.blob();
		const reader = new FileReader();

		return new Promise<void>((resolve, reject) => {
			reader.onload = async (e) => {
				if (typeof e.target?.result === 'string') {
					const binaryStr = atob(e.target.result.replace(/.*base64,/, ''));
					const pages = await renderPages(binaryStr, fileName);
					await handleFilesUpload(getPdfUploadOrder(pdf, pages));
					resolve();
				}
			};
			reader.onerror = () => reject(new Error('Failed to read file'));
			reader.readAsDataURL(blob);
		});
	};

	const base64ToPngFile = (base64Str: string, fileName: string) => {
		const base64Data = base64Str.split(',')[1];
		const binaryStr = atob(base64Data);
		const len = binaryStr.length;
		const u8Array = new Uint8Array(len);

		for (let i = 0; i < len; i++) {
			u8Array[i] = binaryStr.charCodeAt(i);
		}

		return new File([u8Array], fileName, { type: 'image/png' });
	};

	return (
		<TFSection
			title={
				<Flex align="center">
					<Input.Label m={0} required={isRequired}>
						{label || _(msg({ id: 'fb.fileInput.title', message: 'Document' }))}
					</Input.Label>
					{tooltip ? <TooltipIcon style={{ margin: '0 2px 0 4px' }} tooltip={tooltip} /> : undefined}
				</Flex>
			}
			icon={<Icon.IconFile size={18} />}
			actions={
				!isReadOnly &&
				isRequired && (
					<Tooltip
						label={
							document.is_no_document
								? _(msg({ id: 'fb.fileInput.markAsAvailable', message: 'Mark a document as available' }))
								: _(
										msg({
											id: 'fb.fileInput.markAsUnavailable',
											message: 'Mark a document as unavailable',
										}),
									)
						}
						withArrow
						position="left"
					>
						<Button
							id={`${name}-mark-button`}
							size="xs"
							variant="subtle"
							disabled={isReadOnly}
							className={classes.switchButton}
							onClick={toggleAvailability}
						>
							{document.is_no_document
								? _(msg({ id: 'fb.fileInput.markAsAvailable', message: 'Mark a document as available' }))
								: _(
										msg({
											id: 'fb.fileInput.markAsUnavailable',
											message: 'Mark a document as unavailable',
										}),
									)}
						</Button>
					</Tooltip>
				)
			}
		>
			<Controller
				control={formContext.control}
				name={`${name}.files`}
				render={({ field: { value } }) => (
					<Box hidden={document.is_no_document}>
						<Stack gap={6}>
							<Stack gap={6} p={0} align="center">
								{filesInfoQuery.isLoading && <Loader size={14} />}
								{filesInfoQuery.data?.map((f) => (
									<DocumentItem key={f.id} file={f} onDelete={handleFileDelete} isReadOnly={isReadOnly} />
								))}
							</Stack>

							{!isReadOnly ? (
								<FileButton
									accept="image/png, image/jpeg, application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
									resetRef={resetFileRef}
									onChange={async (file) => {
										if (file?.type === 'application/pdf' && isPDFToPNGEnabled) {
											await uploadPDF(file);
										} else {
											await handleFileUpload(file);
										}
									}}
									name={`${name}.files`}
								>
									{(props) => {
										return (
											<Stack gap={9}>
												<Button
													{...props}
													size="sm"
													variant="light"
													fullWidth
													leftSection={!uploadFileMutation.isPending && <Icon.IconUpload size={16} stroke={1.8} />}
													disabled={isReadOnly || uploadFileMutation.isPending}
													className={`${classes.uploadButton} ${
														isRequired && !value?.length && reasonError ? classes.uploadButton__error : ''
													}`}
												>
													{uploadFileMutation.isPending ? (
														<Loader size={14} />
													) : (
														_(
															msg({
																id: 'fb.fileInput.uploadDocumentButton',
																message: 'Upload document',
															}),
														)
													)}
												</Button>
											</Stack>
										);
									}}
								</FileButton>
							) : null}

							{isDocumentCheck ? (
								<DocumentsCheckComponent
									onCheckCreated={handleDocumentCheckCreated}
									isReadOnly={!!isReadOnly}
									files={filesInfoQuery.data ?? []}
									documentCheckId={document.document_check_job?._object}
								/>
							) : null}

							<Box hidden={!(isReadOnly && !document.files.length)}>
								<Box className={classes.emptyWrapper}>
									<Box className={classes.emptyContent}>
										<Trans id="fb.fileInput.noDocuments">No documents uploaded</Trans>
									</Box>
								</Box>
							</Box>
						</Stack>
					</Box>
				)}
			/>

			<Controller
				control={formContext.control}
				name={`${name}.reason`}
				render={({ fieldState: { error } }) => (
					<Box hidden={!document.is_no_document}>
						<Input.Wrapper
							label={_(
								msg({
									id: 'fb.fileInput.missingFileReasonLabel',
									message: 'Reason',
								}),
							)}
							labelProps={{ required: isRequired }}
						>
							<Textarea
								id={`${name}-description`}
								readOnly={isReadOnly}
								error={Boolean(error)}
								className={isReadOnly ? classes.textInputDisabled : ''}
								rightSection={<TooltipIcon tooltip={tooltip} error={error} />}
								placeholder={_(
									msg({
										id: 'fb.fileInput.missingFileReasonPlaceholder',
										message: 'Reason for missing file...',
									}),
								)}
								{...formContext.register(`${name}.reason`)}
							/>
						</Input.Wrapper>
					</Box>
				)}
			/>
		</TFSection>
	);
}
